/**
 * Error-classification helpers and telemetry session wrapper for the viewer-load flow.
 *
 * Keeping the pure classification logic in this module serves two purposes:
 *   1. The classifyFailureForTelemetry function can be unit-tested with zero mocking
 *      (no React, no Redux, no SDK, no OpenTelemetry).
 *   2. useHandleInit's catch block stays concise.
 *
 * Note: The ViewerLoadTelemetrySession class depends on the real `telemetry` module
 * and typically requires mocking in unit tests.
 */

import { isResponseError } from "@procore/core-http";

import { telemetry, type TelemetryFailureDetails } from "telemetry";

import { ErrorKind } from "../sceneError/types";

// ─── InitCanvasError ──────────────────────────────────────────────────────────

/**
 * Wraps a WebGL / canvas-initialisation failure so the outer catch block can
 * distinguish it from SDK or network errors.
 */
export class InitCanvasError extends Error {
    constructor(cause: Error) {
        super("Failed to initialize 3D canvas");
        this.name = "InitCanvasError";
        this.cause = cause;
    }
}

// ─── ForcedSceneError ─────────────────────────────────────────────────────────

/**
 * Query param that names the {@link ErrorKind} to fake, e.g.
 * `?debug=true&novolibForceSceneError=NOT_AUTHORIZED`.
 *
 * Prefixed because the viewer ships as a library inside a host MFE, where an unprefixed
 * name risks colliding with a param another bundle on the page owns.
 */
const FORCE_SCENE_ERROR_PARAM = "novolibForceSceneError";

/**
 * A deliberately triggered scene failure, used to demo and QA the error cards.
 *
 * Engineering, product, and design can walk every card from a URL instead of
 * reproducing seven real backend failures.
 *
 * Carries the requested kind so {@link classifyFailureForTelemetry} can report it
 * verbatim instead of inferring one, which keeps every card reachable — including
 * INIT_CANVAS_ERROR, whose real error type would divert into the GPU-guidance path
 * and suppress the card.
 */
export class ForcedSceneError extends Error {
    constructor(public readonly kind: ErrorKind) {
        super(`Forced scene error: ${kind}`);
        this.name = "ForcedSceneError";
    }
}

/**
 * Reads the requested forced error from the URL, or undefined when none is asked for.
 *
 * Not gated on `import.meta.env.DEV`: the cards have to be verifiable in the
 * library bundle served as a staging MFE override, which is a production build.
 * `debug=true` is required alongside it as a marker of deliberate developer intent,
 * so a param left in a URL on its own does nothing. It is not a safeguard: whoever
 * copies the URL copies both params.
 */
export function readForcedSceneError(): ForcedSceneError | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") !== "true") {
        return undefined;
    }

    const requested = params.get(FORCE_SCENE_ERROR_PARAM);
    if (!requested || !Object.values(ErrorKind).includes(requested as ErrorKind)) {
        return undefined;
    }

    return new ForcedSceneError(requested as ErrorKind);
}

// ─── HTTP status extraction ───────────────────────────────────────────────────

/** Extracts the HTTP status code from SDK error messages of the form `"HTTP Error:404 404"`. */
export function extractHttpStatus(message: string): number | undefined {
    const match = /^HTTP Error:(\d+)/.exec(message);
    return match ? Number.parseInt(match[1], 10) : undefined;
}

// ─── Classification ───────────────────────────────────────────────────────────

export type TelemetryFailureClassification = {
    /** Telemetry failure descriptor – passed directly to telemetry.endViewerLoad(). */
    failure: TelemetryFailureDetails;
    /**
     * Span attributes to stamp before closing the span.
     * Applied by the caller so this function stays side-effect free.
     */
    spanAttributes?: Record<string, string | boolean | number>;
    /** Stack / detail string for the Redux scene-status dispatch (may be undefined). */
    stack?: string;
};

/**
 * Best-effort detection of WebGL2 context loss on a canvas element.
 *
 * **Caveat:** `canvas.getContext(...)` may create a new WebGL2 context (and
 * allocate GPU resources) if one does not already exist.  This is acceptable
 * here because the function is only called from `classifyFailureForTelemetry`,
 * which runs after a failed canvas-init attempt — a context was already
 * created or attempted on this canvas.
 */
function isWebglContextLost(canvas: HTMLCanvasElement | null | undefined): boolean {
    if (!canvas) {
        return false;
    }

    const gl = canvas.getContext("webgl2") as WebGL2RenderingContext | null;
    return !!(gl && gl.isContextLost());
}

// ─── Telemetry session ────────────────────────────────────────────────────────

/** Dimensions the `viewer.ttfr_ms` histogram is sliced by. */
export type FirstRenderDimensions = {
    "device.gpu_tier": number;
    "device.is_mobile": boolean;
};

/**
 * Manages the OpenTelemetry span lifecycle for a single viewer-load attempt,
 * and the one metric — time to first render — that is written alongside it.
 *
 * Wraps the phased API (start → optional stamp → optional markFirstRender →
 * succeed/fail) so that the telemetry wiring can be unit-tested by constructing
 * this class directly with only `telemetry` mocked — no hook render, no Redux,
 * no SDK required.
 */
export class ViewerLoadTelemetrySession {
    public readonly span: ReturnType<typeof telemetry.startViewerLoad>;

    private readonly startedAtMs: number = performance.now();
    private firstRenderMarked: boolean = false;
    private pageHiddenDuringLoad: boolean = false;
    private stopWatchingVisibility: () => void = () => {};

    constructor(sceneId: string) {
        this.span = telemetry.startViewerLoad(sceneId);
        this._watchVisibility();
    }

    /**
     * Track whether the page was ever hidden between load start and first render.
     *
     * `requestAnimationFrame` does not fire in a hidden tab, so a load started in the
     * background resolves its first frame only when the tab is foregrounded — minutes
     * later, in the worst case. Checking visibility at mark time cannot detect this,
     * because the tab is visible again by definition once the frame fires. Recording
     * the flag lets those samples be excluded from percentiles at query time instead
     * of silently inflating the tail.
     */
    private _watchVisibility(): void {
        if (typeof document === "undefined") {
            return;
        }

        this.pageHiddenDuringLoad = document.visibilityState === "hidden";

        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                this.pageHiddenDuringLoad = true;
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        this.stopWatchingVisibility = () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }

    /**
     * Stamps an arbitrary boolean attribute on the active span.
     * Safe to call even if telemetry is disabled (span is null).
     */
    stamp(key: string, value: boolean): void {
        this.span?.setAttribute(key, value);
    }

    /**
     * Record time to first render for this load attempt. Ignores repeat calls.
     *
     * This is deliberately not the load span's duration. The span stays open
     * through viewpoint restoration and a fixed settling delay after the
     * engine starts drawing, so its duration overstates what the user waited
     * for by seconds.
     *
     * It is also not time-to-visible-pixels: an opaque loading overlay covers the
     * canvas until the scene settles, so the frame measured here is drawn but not
     * yet shown. This is the engine-readiness lower bound on the wait.
     *
     * Callers reach this through an un-awaited `requestAnimationFrame`, so it can run
     * after the span is already closed — the metric is still recorded, and the span
     * attribute is dropped by the SDK. See {@link _watchVisibility} for why the
     * `page.was_hidden` dimension is stamped.
     *
     * @param dimensions - Metric dimensions to slice percentiles by (device class).
     */
    markFirstRender(dimensions?: FirstRenderDimensions): void {
        if (this.firstRenderMarked) {
            return;
        }
        this.firstRenderMarked = true;
        this.stopWatchingVisibility();

        const ttfrMs = Math.round(performance.now() - this.startedAtMs);

        this.span?.setAttribute("viewer.ttfr_ms", ttfrMs);
        telemetry.recordTimeToFirstRender(ttfrMs, {
            ...dimensions,
            "page.was_hidden": this.pageHiddenDuringLoad,
        });
    }

    /** Close the span as a success. */
    succeed(): void {
        this.stopWatchingVisibility();
        telemetry.endViewerLoad(this.span, true);
    }

    /**
     * Apply any span attributes from the classification, then close the span
     * as a failure.  Attributes are always applied before the span is closed.
     */
    fail(classification: TelemetryFailureClassification): void {
        this.stopWatchingVisibility();

        for (const [key, value] of Object.entries(classification.spanAttributes ?? {})) {
            this.span?.setAttribute(key, value);
        }
        telemetry.endViewerLoad(this.span, false, classification.failure);
    }
}

/**
 * Maps a caught error to a {@link TelemetryFailureClassification}.
 *
 * No React, Redux, or SDK dependencies — all runtime context is injected via `opts`.
 * Note: may call `canvas.getContext()` for WebGL context-loss detection, which
 * can allocate GPU resources (see {@link isWebglContextLost}).
 *
 * @param e    - The value caught by the outer try/catch.
 * @param opts - Runtime context: translation fn, online flag, and canvas ref.
 */
export function classifyFailureForTelemetry(
    e: unknown,
    opts: {
        t: (key: string) => string;
        isOnline: boolean;
        canvas?: HTMLCanvasElement | null;
    },
): TelemetryFailureClassification {
    const { t, isOnline, canvas } = opts;
    const webglContextLost = isWebglContextLost(canvas);

    // ── Branch 0: deliberately forced error (debug query param) ──────────────
    if (e instanceof ForcedSceneError) {
        return {
            failure: { errorKind: e.kind },
            spanAttributes: { "debug.forced_error": true },
        };
    }

    // ── Branch 1: structured object error (RTK Query / API responses) ─────────
    if (e && typeof e === "object" && "error" in e) {
        const { error, statusCode } = e as { error: string; statusCode: number };

        if (
            statusCode === 401 ||
            statusCode === 403 ||
            error === "Not authorized" ||
            error === t("api.error.notAuthorized")
        ) {
            return {
                failure: {
                    errorKind: ErrorKind.NOT_AUTHORIZED,
                    statusCode: statusCode === 401 || statusCode === 403 ? statusCode : undefined,
                },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
            };
        }

        if (statusCode === 404 || error === "Scene not found" || error === t("api.error.sceneNotFound")) {
            return {
                failure: {
                    errorKind: ErrorKind.INVALID_SCENE,
                    statusCode: statusCode === 404 ? statusCode : undefined,
                },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
            };
        }

        if (statusCode === 410 || error === "Scene deleted" || error === t("api.error.sceneDeleted")) {
            return {
                failure: {
                    errorKind: ErrorKind.DELETED_SCENE,
                    statusCode: statusCode === 410 ? statusCode : undefined,
                },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
            };
        }

        return {
            failure: {
                errorKind: isOnline ? ErrorKind.UNKNOWN_ERROR : ErrorKind.OFFLINE_UNAVAILABLE,
                statusCode,
            },
            spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
        };
    }

    // ── Branch 2: Error instance (SDK throws, canvas init, generic JS errors) ─
    if (e instanceof Error) {
        // Procore REST calls throw a core-http ResponseError whose message is only "[404]".
        const httpStatus = isResponseError(e) ? e.status : extractHttpStatus(e.message);

        if (httpStatus === 401 || httpStatus === 403) {
            return { failure: { errorKind: ErrorKind.NOT_AUTHORIZED, statusCode: httpStatus } };
        }

        if (httpStatus === 404) {
            return { failure: { errorKind: ErrorKind.EMPTY_PROJECT, statusCode: httpStatus } };
        }

        if (httpStatus === 410) {
            return { failure: { errorKind: ErrorKind.DELETED_SCENE, statusCode: httpStatus } };
        }

        if (e instanceof InitCanvasError) {
            return {
                failure: { errorKind: ErrorKind.INIT_CANVAS_ERROR },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
                stack: e.cause ? (e.cause as Error).stack : undefined,
            };
        }

        // Generic JS error
        const errorKind = isOnline ? ErrorKind.UNKNOWN_ERROR : ErrorKind.OFFLINE_UNAVAILABLE;
        const stack = e.stack ?? (typeof e.cause === "string" ? e.cause : `${e.name}: ${e.message}`);
        const sanitizedMessage = e.message.slice(0, 512);
        return {
            failure: { errorKind, message: sanitizedMessage },
            spanAttributes: { "error.message": sanitizedMessage },
            stack,
        };
    }

    // ── Branch 3: non-Error, non-object (string / number / plain object) ──────
    let stack: string | undefined;
    try {
        stack = JSON.stringify(e);
    } catch {
        stack = String(e);
    }
    return {
        failure: { errorKind: isOnline ? ErrorKind.UNKNOWN_ERROR : ErrorKind.OFFLINE_UNAVAILABLE },
        stack,
    };
}
